import { ChangeDetectionStrategy, Component, TemplateRef, computed, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { HlmButtonDirective } from '@spartan-ng/helm/button';
import { HlmTooltipTriggerDirective } from '@spartan-ng/helm/tooltip';
import { TemplateStore } from '@features/catalog/store/template.store';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import { ColumnDef, DataTableComponent } from '@shared/components/data-table/data-table';
import { InlineNotice } from '@shared/components/inline-notice/inline-notice';
import { SidePanel } from '@shared/components/side-panel/side-panel';
import { CampaignsService } from '../../services/campaigns.service';
import { CampaignWorkflowStore } from '../../store/campaign-workflow.store';
import { PIPELINE_STEPS, PIPELINE_TASKS, REVIEW_STAGES, STAGE_META, WORKFLOW_STAGES, WorkItem, WorkflowStage, runDuration, runKind, runStatus } from '../../models/campaign-workflow.model';
import { StageChip } from '../stage-chip/stage-chip';

const PAGE_SIZE = 10;
const fmt = (n: number) => n.toLocaleString('de-DE');

/** One execution: how it was set up, what came out of it and where its analyses stand now. */
@Component({
  selector: 'app-campaign-run-detail',
  standalone: true,
  imports: [DatePipe, DecimalPipe, HlmButtonDirective, HlmTooltipTriggerDirective, DataTableComponent, InlineNotice, SidePanel, StageChip],
  templateUrl: './campaign-run-detail.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CampaignRunDetail {
  readonly store = inject(CampaignWorkflowStore);
  private readonly api = inject(CampaignsService);
  private readonly templates = inject(TemplateStore);
  private readonly org = inject(CurrentOrganizationStore);

  readonly pageSize = PAGE_SIZE;
  readonly stageTpl = viewChild.required<TemplateRef<unknown>>('stageTpl');
  readonly members = signal<string[] | null>(null);
  readonly error = signal<string | null>(null);

  readonly run = computed(() => this.store.runs().find((run) => run.runId === this.store.runDetailId()) ?? null);
  readonly status = computed(() => { const run = this.run(); return run ? runStatus(run) : null; });

  readonly facts = computed(() => {
    const run = this.run();
    if (!run) return [];
    const step = [...PIPELINE_TASKS, ...PIPELINE_STEPS].find((candidate) => candidate.last === run.endTask)?.label ?? `Schritt ${run.endTask}`;
    const template = run.templateId ? this.templates.allTemplates().find((candidate) => candidate.id === run.templateId)?.name ?? '–' : 'Wie je Analyse hinterlegt';
    return [
      { key: 'Art', value: runKind(run) },
      { key: 'Bis Schritt', value: step },
      { key: 'Analysedatei', value: run.analysisType?.toUpperCase() ?? '–' },
      { key: 'Vorlage', value: template },
      { key: 'Dauer', value: runDuration(run) },
      { key: 'Gestartet von', value: run.createdBy.name ?? '–' },
    ];
  });

  readonly outcome = computed(() => {
    const run = this.run();
    if (!run) return [];
    return [
      // Older runs only recorded what they dispatched.
      run.action || run.succeeded
        ? { label: 'Ergebnisse', count: run.succeeded, color: STAGE_META.done.color }
        : { label: 'Gestartet', count: run.dispatched, color: STAGE_META.done.color },
      { label: 'Fehler', count: run.failed, color: STAGE_META.error.color },
      { label: 'Ausgelassen', count: run.skipped, color: STAGE_META.new.color },
    ];
  });

  readonly rows = computed<WorkItem[]>(() => {
    const items = this.store.itemById();
    return (this.members() ?? []).flatMap((id) => { const item = items.get(id); return item ? [item] : []; });
  });
  readonly stages = computed(() => {
    const rows = this.rows();
    return WORKFLOW_STAGES.flatMap((stage) => {
      const count = rows.filter((row) => row.stage === stage).length;
      return count ? [{ stage, count, meta: STAGE_META[stage], share: rows.length ? Math.max(2, (count / rows.length) * 100) : 0 }] : [];
    });
  });
  readonly toReview = computed(() => this.rows().filter((row) => REVIEW_STAGES.includes(row.stage)).map((row) => row.id));
  readonly failed = computed(() => this.rows().filter((row) => row.stage === 'error').map((row) => row.id));

  readonly columns = computed<ColumnDef<WorkItem>[]>(() => [
    { header: 'Adresse', cell: (item) => item.title ?? 'Ohne Adresse', searchValue: (item) => item.title ?? '', sortValue: (item) => item.title },
    { header: 'Stufe', template: this.stageTpl(), class: 'w-[132px]', sortValue: (item) => item.stage },
    { header: 'Leistung', cell: (item) => (item.planning.kwp !== null ? `${item.planning.kwp.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kWp` : '–'), class: 'w-[92px] whitespace-nowrap text-right font-mono', sortValue: (item) => item.planning.kwp },
  ]);
  readonly getRowId = (item: WorkItem) => item.id;

  readonly reviewLabel = computed(() => `${fmt(this.toReview().length)} prüfen`);
  readonly retryLabel = computed(() => `${fmt(this.failed().length)} Fehler erneut`);

  constructor() {
    void this.store.loadItems();
    const org = this.org.organizationId();
    if (org) this.templates.loadTemplatesForOrg(org);
    effect(() => {
      const runId = this.store.runDetailId();
      untracked(() => void this.load(runId));
    });
  }

  private async load(runId: string | null): Promise<void> {
    this.members.set(null);
    this.error.set(null);
    const org = this.store.organizationId();
    const campaign = this.store.campaignId();
    if (!runId || !org || !campaign) return;
    try {
      const ids = await this.api.getBulkRunAnalysisIds(org, campaign, runId);
      if (this.store.runDetailId() === runId) this.members.set(ids);
    } catch {
      if (this.store.runDetailId() === runId) this.error.set('Die Analysen dieser Berechnung konnten nicht geladen werden.');
    }
  }

  close(): void {
    this.store.runDetailId.set(null);
  }

  showStage(stage: WorkflowStage): void {
    void this.store.go('analyses', { stage, runId: this.store.runDetailId() ?? undefined });
  }

  showAll(): void {
    void this.store.go('analyses', { runId: this.store.runDetailId() ?? undefined });
  }

  review(): void {
    void this.store.go('review', { ids: this.toReview() });
  }

  retry(): void {
    this.store.openRun({ ids: this.failed(), target: 'error' });
  }

  open(item: WorkItem): void {
    const stage = item.stage;
    if (stage === 'done') void this.store.go('plannings', { focus: item.id });
    else if (REVIEW_STAGES.includes(stage) || stage === 'replan') void this.store.go('review', { stage, focus: item.id });
    else void this.store.go('analyses', { stage, runId: this.store.runDetailId() ?? undefined });
  }
}
